import {yupResolver} from '@hookform/resolvers/yup'
import {HubConnection, HubConnectionBuilder, JsonHubProtocol, LogLevel} from '@microsoft/signalr'
import ClearIcon from '@mui/icons-material/Clear'
import {Alert, Box, Button, Card, CardContent, Dialog, DialogContent, Grid, IconButton, Typography} from '@mui/material'
import {useEffect, useState} from 'react'
import {FieldValues, useForm} from 'react-hook-form'
import {toast} from 'react-toastify'
import {useRecoilValue} from 'recoil'
import {scorecardResponse} from '../../../Component/Model/Dto/ScorecardLicense'
import {UserAtom} from '../../../State/user.Atom'
import {defaultObject} from '../../../Component/Model/helpers'
import {ImportService} from '../../../Component/Api/Import/ImportService'
import {emptyGuid} from '../../../Component/Common/Constants/Constants'
import {dataImportTypeLookupVal, getUploadFileName, ImportCommonFunctionsDropDown} from '../../../Helpers/ImportHelper'
import {DataImportType} from '../../../Component/Model/Enum/BEE123Enums'
import {IntermediateGlobalLoading} from '../../../Component/Common/Components/Loading/IntermediateGlobalLoading'
import AppMultiSelect from '../../../Component/Common/Components/FormValidationInputs/AppMultiSelect'
import {getIOptionsArray} from '../../../Helpers/arrayHelper'
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList'
import {MDTypography} from '../../../Component/Common/Components/MDTypography'
import {TooltipLoadingButton} from '../../../Component/Common/Components/Tooltip/TooltipLoadingButton'
import {ImportProgressBar, onDownload} from '../../../Component/Common/Components/ProgressBar/ImportProgressBar'
import AppDropzone from '../../../Component/Common/Components/FormValidationInputs/AppDropZone'
import {MDAlert} from '../../../Component/Common/Components/MDAlert'
import {MDBox} from '../../../Component/Common/Components/MDBox'
import {DataImportTypesCode} from '../../Common/ImportAndExport/ImportPopup'
import {
    VerificationWorkflowService
} from '../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService'
import {CoreImportService, EmploymentEquityGroupImportData} from '../../../Component/Api/Import/CoreImportService'
import {ExportKeyValue} from '../../../Component/Model/Dto/Upload'
import config from '../../../config'
import {BEEScorecardService} from '../../../Component/Api/BEEScorecard/BEEScorecard'
import {GroupImportValidationSchema} from './GroupImportValidationSchema'
import {ImportResult} from '../../VerificationAgency/VerificationProcess/VAClientImportManagement/MultiPageImport'
import {MDButton} from '../../../Component/Common/Components/MDButton'
import {CustomDialogTitle} from '../../Common/Custom/CustomDialogTitle'
import {
    IndividualSalariesTemplateModal
} from '../../CentralDataManagement/IndividualSalaries/IndividualSalariesTemplateModal'
import {ImportSource} from "../../../Component/Model/Enum/ImportEnum";
import {useSignalRConnection} from "../../../Hooks/useSignalRConnection";

export interface ProgressData {
    progress: number;
    numberOfRowsCorrect: number;
    numberOfRowsFailed: number;
    log: any[];
    isSuccess: boolean;
    errorLogUrl?: string;
    lastSuccessfulStep?: string;
}

interface IProps {
    dataImportType: DataImportType
}

export const GroupImport = (props: IProps) => {

    const user = useRecoilValue(UserAtom);
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const [scorecardData, setScorecardData] = useState<scorecardResponse[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [scorecardList, setScorecardList] = useState<string[]>([]);
    const [progressData, setProgressData] = useState<{ [key: string]: ProgressData }>({});
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [importResult, setImportResult] = useState<ImportResult>();
    const [openSalariesTemplateModal, setOpenSalariesTemplateModal] = useState<boolean>(false);
    const commonIdentifier = `${user.username}-eeGroupImport-${DataImportType[props.dataImportType]}`;
    const hub = useSignalRConnection("ImportHub");

    const { control, handleSubmit, watch, setValue} = useForm({
        mode: 'all',
        resolver: yupResolver(GroupImportValidationSchema)
    });
    const file = watch('file');

    const findScorecardName = (scorecardId: string): string => {
        const scorecard = scorecardData.find((x) => x.scorecardId === scorecardId);
        if (scorecard) {
            return scorecard.scorecardName;
        }
        return "";
    }

    useEffect(() => {
        getScorecardData();
    }, [props.dataImportType]);

    const getScorecardData = async () => {
        try {
            setIsLoading(true);
            const result = await BEEScorecardService.getAllScorecards();
            setScorecardData(result.data.eeScorecards);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearFile = () => {
        setValue('file', null);
    };


    useEffect(() => {
        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                hub.on(`importProgress-${commonIdentifier}`, (data: any) => {
                    const {CurrentScorecard, progress, LastSuccessfulStep} = data;
                    setProgressData((prevData) => ({
                        ...prevData, [CurrentScorecard]: {
                            ...prevData[CurrentScorecard],
                            progress,
                            lastSuccessfulStep: LastSuccessfulStep
                        }
                    }));
                });
                hub.on(`importProgressFailed-${commonIdentifier}`, (data: any) => {
                    const {
                        CurrentScorecard,
                        NumberOfRowsCorrect,
                        NumberOfRowsFailed,
                        Log,
                        ErrorBlobUrl,
                        LastSuccessfulStep
                    } = data;
                    setProgressData((prevData) => ({
                        ...prevData,
                        [CurrentScorecard]: {
                            progress: 0,
                            isSuccess: false,
                            numberOfRowsCorrect: NumberOfRowsCorrect,
                            numberOfRowsFailed: NumberOfRowsFailed,
                            log: Log,
                            errorLogUrl: ErrorBlobUrl,
                            lastSuccessfulStep: LastSuccessfulStep
                        }
                    }));
                    const currentIndex = scorecardList.indexOf(CurrentScorecard);
                    if (currentIndex < scorecardList.length - 1) {
                        const nextScorecardId = scorecardList[currentIndex + 1];
                        // Start progress for the next scorecardId
                        setProgressData((prevData) => ({
                            ...prevData,
                            [nextScorecardId]: {
                                progress: 0,
                                isSuccess: true,
                                numberOfRowsCorrect: 0,
                                numberOfRowsFailed: 0,
                                log: []
                            }
                        }));
                    }
                });
                hub.on(`importProgressSuccess-${commonIdentifier}`, (data: any) => {
                    const {CurrentScorecard, NumberOfRowsCorrect, NumberOfRowsFailed, Log} = data;
                    setProgressData((prevData) => ({
                        ...prevData,
                        [CurrentScorecard]: {
                            progress: 100,
                            isSuccess: true,
                            numberOfRowsCorrect: NumberOfRowsCorrect,
                            numberOfRowsFailed: NumberOfRowsFailed,
                            log: Log
                        }
                    }));
                    const currentIndex = scorecardList.indexOf(CurrentScorecard);
                    if (currentIndex < scorecardList.length - 1) {
                        const nextScorecardId = scorecardList[currentIndex + 1];
                        // Start progress for the next scorecardId
                        setProgressData((prevData) => ({
                            ...prevData,
                            [nextScorecardId]: {
                                progress: 0,
                                isSuccess: true,
                                numberOfRowsCorrect: 0,
                                numberOfRowsFailed: 0,
                                log: []
                            }
                        }));
                    }

                });
                hub.on(`importComplete-${commonIdentifier}`, () => {
                    setIsImporting(false);
                });
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, []);

    const handleDataSubmit = async (data: FieldValues) => {
        setProgressData(defaultObject);
        setImportResult(defaultObject);
        setIsImporting(true);
        try {
            const parameters: ExportKeyValue[] = [];
            const scorecardIds = [...data.scorecards];
            setScorecardList(scorecardIds);
            const inProgressScorecards: string[] = [];
            for (const scorecardId of scorecardIds) {
                const isInProgress = await checkForInProgressImport(scorecardId);
                if (isInProgress) {
                    inProgressScorecards.push(scorecardId);
                }
            }

            if (inProgressScorecards.length > 0) {
                for (const id of inProgressScorecards) {
                    toast.error(`An import is already in progress for scorecard: ${findScorecardName(id)}`);
                }
                setIsImporting(false);
                return;
            }

            const request: EmploymentEquityGroupImportData = {
                importType: props.dataImportType,
                scorecardIds: scorecardIds,
                importSource: ImportSource.EE_GroupImport,
                importAction: parseInt(data.importType),
                file: data.file,
                parameters: parameters
            };

            const result = await CoreImportService.ImportEmploymentEquityGroupFile(request);
            setImportResult(result.data);
            if (!result.data.wasSuccessful) {
                setIsImporting(false);
            }

        } catch (error) {
            console.log(error);
            setIsImporting(false);
            toast.error('Import Failed');
        }
    }

    const checkForInProgressImport = async (scorecardId: string): Promise<boolean> => {
        try {
            const result = await VerificationWorkflowService.IsImportInProgressForScorecard(scorecardId);
            return !!result.data;
        } catch (error) {
            console.log(error);
            toast.error("An error has occurred");
            return false;
        }
    };

    const getImportHelpFile = (e: any) => {
        const sectionId = dataImportTypeLookupVal(DataImportType[getFileDownloadDataImportType()], DataImportTypesCode.name, DataImportTypesCode.id);
        if (sectionId?.toString() !== undefined) {
            ImportService.DownloadImportHelpfile(emptyGuid, sectionId.toString());
        }
    };

    const getImportTemplate = (e: any) => {
        if (props.dataImportType == DataImportType.EESalary) {
            setOpenSalariesTemplateModal(true);
        } else {
            ImportService.DownloadImportTemplate(emptyGuid, getUploadFileName(DataImportType[getFileDownloadDataImportType()]));
        }
    };

    const disableImportButton = (): boolean => {
        if (scorecardData === undefined && scorecardData === undefined) {
            return true;
        }
        return scorecardData?.length === 0;
    }

    const closeTemplateModal = () => {
        setOpenSalariesTemplateModal(false);
    }

    const getFileDownloadDataImportType = (): DataImportType => {
        switch (props.dataImportType) {
            case DataImportType.EESalary:
                return DataImportType.EESalaryGroupImport;
            case DataImportType.EEIndividualGroupImport:
            default:
                return DataImportType.EEIndividualGroupImport;
        }
    }

    if (isLoading) {
        return <IntermediateGlobalLoading/>;
    }
    ;
    return (
        <>
            <Grid>
                <Typography variant="body2">Please note, locked and expired scorecards are not available for
                    selection. </Typography>
            </Grid>
            <Grid container spacing={3} component="form" id='GroupImportForm' onSubmit={handleSubmit(handleDataSubmit)}>

                <Grid item xs={6}>
                    {scorecardData && scorecardData.length > 0 && (
                        <Box mt={2}>
                            <AppMultiSelect
                                disabled={isImporting}
                                control={control}
                                hintText='Search or select an option'
                                name="scorecards"
                                label="Scorecards (select all included in your import file)"
                                options={getIOptionsArray(scorecardData, "scorecardId", "scorecardName")}
                                size="small"
                            />
                        </Box>
                    )}
                    <Box mt={2}>
                        {disableImportButton() &&
                            <Typography
                                style={{fontSize: '15px', color: 'red', paddingTop: '30px', paddingBottom: '20px'}}>
                                You don't have any scorecards assigned to you, please contact your administrator to
                                create a scorecard
                            </Typography>
                        }
                    </Box>
                    <Box mt={2}>
                        <AppSelectList
                            disabled={isImporting}
                            control={control} name="importType"
                            label="Import Type"
                            items={ImportCommonFunctionsDropDown(DataImportType[props.dataImportType])}
                            size="small"
                        />
                        <MDBox mt={4} color="white">
                            <MDAlert color='light'>
                                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
                                    <p style={{marginBottom: "0.2rem"}}> Click<Button disabled={isImporting}
                                                                                      variant="text"
                                                                                      onClick={getImportHelpFile}
                                                                                      size="small" style={{
                                        fontSize: "inherit",
                                        padding: "0.25rem",
                                        paddingBottom: "0.4rem",
                                        minWidth: "0",
                                        textTransform: "none"
                                    }}>here</Button>
                                        for import instructions</p>
                                    <p style={{marginBottom: "0.2rem"}}> Click<Button disabled={isImporting}
                                                                                      variant="text"
                                                                                      onClick={getImportTemplate}
                                                                                      size="small" style={{
                                        fontSize: "inherit",
                                        padding: "0.25rem",
                                        paddingBottom: "0.4rem",
                                        minWidth: "0",
                                        textTransform: "none"
                                    }}>here</Button>
                                        to download the template file</p>
                                </MDTypography>
                            </MDAlert>
                        </MDBox>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box mt={2} display='flex' justifyContent='space-between' alignItems='center'>
                        {!file && <AppDropzone control={control} name="file"/>}
                        {file && (
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                                    <Box position="relative">
                                        <img src={`${process.env.PUBLIC_URL}/img/csv-icon.png`} alt='preview'
                                             style={{maxHeight: '200px'}}/>
                                        <IconButton
                                            disabled={isImporting}
                                            onClick={clearFile}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                zIndex: 1
                                            }}
                                        >
                                            <ClearIcon/>
                                        </IconButton>
                                    </Box>
                                </Box>
                                <MDTypography variant="caption" align="center">{file.name}</MDTypography>
                            </Box>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={5}>
                    <TooltipLoadingButton buttonText="Begin Import" form="GroupImportForm"
                                          tooltipTitle="No scorecards available" disabled={disableImportButton()}
                                          isLoading={isImporting} showTooltipOnDisabled={true}/>
                </Grid>
                {(importResult?.log?.length ?? 0) > 0 &&
                    <Grid item xs={12} xl={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <MDTypography variant="h6" mb={1}>General Errors</MDTypography>
                                <MDBox>
                                    <Alert severity="error" sx={{mb: 1}} icon={false}>
                                        <Grid container>
                                            <Grid item xs={12} lg={12}>
                                                <MDTypography component='span' variant='h5' color='error'>
                                                    Import Failed - Data Validation Failed
                                                </MDTypography>
                                                {(importResult?.numberOfRowsFailed ?? 0) > 0 && (
                                                    <MDBox mb={1}>
                                                        <MDTypography component='p' variant='body' fontWeight='bold'
                                                                      color='error'>
                                                            Number of failed rows: {importResult?.numberOfRowsFailed}
                                                        </MDTypography>
                                                    </MDBox>
                                                )}
                                                {importResult?.errorLogUrl !== null && (
                                                    <MDBox mb={1}>
                                                        <MDBox mt={1}>
                                                            <MDButton
                                                                color='error'
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    onDownload(importResult?.errorLogUrl!)
                                                                }}
                                                            >
                                                                Download Error Log
                                                            </MDButton>
                                                        </MDBox>
                                                    </MDBox>
                                                )}
                                                {importResult?.log?.map((error) => (
                                                    <MDTypography component='p' variant='body' fontWeight='regular'
                                                                  mb={1} color='error'>
                                                        {error}
                                                    </MDTypography>
                                                ))}
                                            </Grid>
                                        </Grid>
                                    </Alert>
                                </MDBox>
                            </CardContent>
                        </Card>
                    </Grid>
                }
                <Grid item xs={12}>
                    <Box mt={2}>
                        {importResult?.wasSuccessful && scorecardList.map((scorecardId) => (
                            <Box key={scorecardId} mb={2}>
                                <Card key={scorecardId} variant="outlined">
                                    <CardContent>
                                        <MDTypography variant="h6">{findScorecardName(scorecardId)}</MDTypography>
                                        <Box sx={{minWidth: 40}}>
                                            <Typography variant="body2"
                                                        color="text.secondary">{`${progressData[scorecardId]?.lastSuccessfulStep ?? ""}`}</Typography>
                                        </Box>
                                        <ImportProgressBar
                                            value={progressData[scorecardId]?.progress ?? 0}
                                            progressData={progressData[scorecardId]}
                                            errorLogUrl={progressData[scorecardId]?.errorLogUrl}
                                        />
                                    </CardContent>
                                </Card>
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>
            <Dialog open={openSalariesTemplateModal} onClose={() => closeTemplateModal()}
                    aria-labelledby="form-dialog-title" fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={() => closeTemplateModal()}>
                    Download Salary Template
                </CustomDialogTitle>
                <DialogContent>
                    <IndividualSalariesTemplateModal closeTemplateModal={closeTemplateModal}
                                                     importSource={ImportSource.EE_GroupImport}/>
                </DialogContent>
            </Dialog>
        </>
    )
}

